/**
 * External write: preview → confirm → poll.
 *
 * Writes are off by default. Each app needs deployment config
 * `writes_enabled: true` after separate live certification, so this panel is
 * built to be told "no" — a `writes_disabled` or `unsupported_operation`
 * response hides the controls rather than presenting a broken button.
 *
 * Three safety properties, not conveniences:
 *
 *   • **The preview's fields are rendered exactly as returned, as text.** HTML
 *     and content are never injected live into the page — they are shown in a
 *     `<pre>`, so a generated document cannot execute or restyle anything here.
 *   • **Confirm is a separate, deliberate click**, and sends only the
 *     `preview_hash` the preview gave. The hash is never recomputed.
 *   • **`outcome_unknown` is never retried automatically.** The provider write
 *     may already have happened, so the user is told to check at the provider
 *     and no retry is offered at all.
 *
 * There is no send, publish, delete or subscribe operation, and nothing here
 * invents one.
 */

import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, Clock, Eye, Send, ShieldAlert } from "lucide-react";
import { Button } from "components/ui/button";
import { SelectField } from "components/ui/select-field";
import { toast } from "components/ui/toast";
import {
    LONG_FIELD_MAX,
    SHORT_FIELD_MAX,
    describeActionStatus,
    describeFailure,
    operationsFor,
    useAction,
    useConfirmAction,
    usePreviewAction,
    useSources,
    validateActionFields,
} from "core/features/integrations";
import type { ActionSchema, AppId, ExternalAction } from "core/features/integrations";

export interface ExternalWritePanelProps {
    appId: AppId;
    appName: string;
}

export function ExternalWritePanel({ appId, appName }: ExternalWritePanelProps) {
    const schemas = useMemo(() => operationsFor(appId), [appId]);
    const [schema, setSchema] = useState<ActionSchema | null>(schemas[0] ?? null);
    const [fields, setFields] = useState<Record<string, string>>({});
    const [action, setAction] = useState<ExternalAction | null>(null);
    /** Set when the backend says writes are off for this app. */
    const [writesOff, setWritesOff] = useState<string | null>(null);

    const preview = usePreviewAction();
    const confirm = useConfirmAction();
    const polled = useAction(action && action.status !== "pending" ? action.id : null);

    const current = polled.data ?? action;
    /* Every field value must be a nonempty string, so an optional field the
       user typed in and then cleared is left out rather than sent as "". */
    const cleanFields = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(fields)
                    .map(([key, value]) => [key, value.trim()] as const)
                    .filter(([, value]) => value.length > 0),
            ),
        [fields],
    );
    const validation = useMemo(
        () => (schema ? validateActionFields(appId, schema.operation, cleanFields) : null),
        [appId, cleanFields, schema],
    );

    /* WordPress.com's sources are its site IDs, used for draft creation only. */
    const sites = useSources(appId, { enabled: appId === "wordpress" });
    const siteOptions = appId === "wordpress" ? sites.data?.sources ?? [] : [];

    const takePreview = useCallback(async () => {
        if (!schema || !validation?.valid) return;
        try {
            const result = await preview.mutateAsync({
                appId,
                operation: schema.operation,
                fields: cleanFields,
            });
            setAction(result);
        } catch (error) {
            const failure = describeFailure(error);
            if (failure.treatment === "writes_disabled") {
                setWritesOff(failure.message);
                return;
            }
            toast({
                title: "Couldn’t prepare this",
                description: failure.message,
                variant: "error",
            });
        }
    }, [appId, cleanFields, preview, schema, validation?.valid]);

    const doConfirm = useCallback(async () => {
        if (!current) return;
        try {
            // Only the hash from the preview. Never recomputed.
            const result = await confirm.mutateAsync({
                actionId: current.id,
                previewHash: current.preview_hash,
            });
            setAction(result);
            // 202 — queued or executing, not completed.
            toast({ title: "Queued", description: "This has been sent for processing." });
        } catch (error) {
            const failure = describeFailure(error);
            if (failure.treatment === "repreview") {
                setAction(null);
                toast({
                    title: "Take a fresh preview",
                    description: failure.message,
                    variant: "error",
                });
                return;
            }
            toast({
                title: "Couldn’t confirm",
                description: failure.message,
                variant: "error",
            });
        }
    }, [confirm, current]);

    if (schemas.length === 0) return null;

    if (writesOff) {
        return (
            <section className="rounded-xl border border-line p-3">
                <h3 className="text-ui font-semibold text-content">Write to {appName}</h3>
                <p className="mt-1 flex items-start gap-1.5 text-meta text-content-muted">
                    <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    {writesOff}
                </p>
            </section>
        );
    }

    const status = current ? describeActionStatus(current) : null;
    const awaitingConfirm = current?.status === "pending";

    return (
        <section className="rounded-xl border border-line p-3">
            <h3 className="text-ui font-semibold text-content">Write to {appName}</h3>

            {schemas.length > 1 && (
                <SelectField
                    aria-label="Operation"
                    value={schema?.operation ?? ""}
                    onChange={(event) => {
                        setSchema(
                            schemas.find((s) => s.operation === event.target.value) ?? null,
                        );
                        setFields({});
                        setAction(null);
                    }}
                    className="mt-2 h-10 w-full rounded-xl border border-line bg-surface px-3 text-ui text-content focus-ring"
                >
                    {schemas.map((option) => (
                        <option key={option.operation} value={option.operation}>
                            {option.label}
                        </option>
                    ))}
                </SelectField>
            )}

            {schema && !current && (
                <>
                    <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-meta text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {schema.warning}
                    </p>

                    <div className="mt-3 space-y-3">
                        {schema.fields.map((field) => (
                            <div key={field.key}>
                                <label
                                    htmlFor={`action-${field.key}`}
                                    className="block text-meta font-medium text-content"
                                >
                                    {field.label}
                                    {field.required && " *"}
                                </label>
                                {field.key === "site_id" && siteOptions.length > 0 && (
                                    <SelectField
                                        aria-label="Your sites"
                                        value={
                                            siteOptions.some((o) => o.id === fields.site_id)
                                                ? fields.site_id
                                                : ""
                                        }
                                        onChange={(event) =>
                                            setFields((f) => ({ ...f, site_id: event.target.value }))
                                        }
                                        className="mt-1 h-9 w-full rounded-lg border border-line bg-surface px-2.5 text-meta text-content focus-ring"
                                    >
                                        <option value="">Choose a site…</option>
                                        {siteOptions.map((site) => (
                                            <option key={site.id} value={site.id}>
                                                {site.name}
                                            </option>
                                        ))}
                                    </SelectField>
                                )}
                                {field.long ? (
                                    <textarea
                                        id={`action-${field.key}`}
                                        value={fields[field.key] ?? ""}
                                        onChange={(event) =>
                                            setFields((f) => ({ ...f, [field.key]: event.target.value }))
                                        }
                                        rows={6}
                                        maxLength={LONG_FIELD_MAX}
                                        className="mt-1 w-full rounded-lg border border-line bg-surface px-2.5 py-2 font-mono text-meta text-content focus-ring"
                                    />
                                ) : (
                                    <input
                                        id={`action-${field.key}`}
                                        value={fields[field.key] ?? ""}
                                        onChange={(event) =>
                                            setFields((f) => ({ ...f, [field.key]: event.target.value }))
                                        }
                                        maxLength={SHORT_FIELD_MAX}
                                        className="mt-1 h-9 w-full rounded-lg border border-line bg-surface px-2.5 text-meta text-content focus-ring"
                                    />
                                )}
                                {field.hint && (
                                    <p className="mt-1 text-meta text-content-muted">{field.hint}</p>
                                )}
                                {validation?.errors[field.key] && (
                                    <p role="alert" className="mt-1 text-meta text-red-600 dark:text-red-400">
                                        {validation.errors[field.key]}
                                    </p>
                                )}
                            </div>
                        ))}
                        {validation?.errors._ && (
                            <p role="alert" className="text-meta text-red-600 dark:text-red-400">
                                {validation.errors._}
                            </p>
                        )}
                    </div>

                    <Button
                        size="sm"
                        variant="secondary"
                        className="mt-3"
                        loading={preview.isPending}
                        disabled={!validation?.valid}
                        onClick={() => void takePreview()}
                    >
                        <Eye className="h-4 w-4" aria-hidden="true" />
                        Preview
                    </Button>
                </>
            )}

            {current && (
                <div className="mt-3">
                    <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2.5 py-2 text-meta text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {/* The server's own warning, shown as given. */}
                        {current.warning}
                    </p>

                    <p className="mt-2 flex items-center gap-1.5 text-meta text-content-muted">
                        <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        Expires {new Date(current.expires_at * 1000).toLocaleTimeString()}
                    </p>

                    {/* Exactly as returned, and as text: a generated document must
                        not run or restyle anything on this page. */}
                    <dl className="mt-3 space-y-2">
                        {Object.entries(current.fields).map(([key, value]) => (
                            <div key={key}>
                                <dt className="text-meta font-medium text-content">{key}</dt>
                                <dd>
                                    <pre className="mt-0.5 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-surface-muted px-2.5 py-2 font-mono text-meta text-content">
                                        {String(value)}
                                    </pre>
                                </dd>
                            </div>
                        ))}
                    </dl>

                    {status && (
                        <p className="mt-3 text-meta text-content-muted">
                            <span className="font-medium text-content">{status.label}</span> —{" "}
                            {status.detail}
                        </p>
                    )}

                    {status?.showResult && current.result_id && (
                        <p className="mt-1 text-meta text-content-muted">
                            Created: <span className="font-medium text-content">{current.result_id}</span>
                        </p>
                    )}

                    {current.error_code && (
                        <p className="mt-1 text-meta text-content-muted">
                            Reported code:{" "}
                            <span className="font-medium text-content">{current.error_code}</span>
                        </p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {/* A separate deliberate click, only while pending. */}
                        {awaitingConfirm && (
                            <Button
                                size="sm"
                                variant="primary"
                                loading={confirm.isPending}
                                onClick={() => void doConfirm()}
                            >
                                <Send className="h-4 w-4" aria-hidden="true" />
                                Confirm
                            </Button>
                        )}

                        {/* `retry` is false for outcome_unknown by design. */}
                        {status?.retry && (
                            <Button size="sm" variant="secondary" onClick={() => setAction(null)}>
                                Start over
                            </Button>
                        )}

                        {current.status === "expired" && (
                            <Button size="sm" variant="secondary" onClick={() => setAction(null)}>
                                Take a fresh preview
                            </Button>
                        )}

                        <Button size="sm" variant="ghost" onClick={() => setAction(null)}>
                            {awaitingConfirm ? "Cancel" : "Close"}
                        </Button>
                    </div>

                    {status?.verifyAtProvider && (
                        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-red-50 px-2.5 py-2 text-meta text-red-700 dark:bg-red-950/40 dark:text-red-300">
                            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            Check in {appName} before doing this again — it will not be retried
                            automatically.
                        </p>
                    )}
                </div>
            )}
        </section>
    );
}
